import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";

type Padding = "none" | "sm" | "md" | "lg";

const PADDING: Record<Padding, string> = {
  none: "",
  sm: "p-4",
  md: "p-5",
  lg: "p-6",
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: Padding;
  /** Lift on hover — for cards that are links or open something. */
  interactive?: boolean;
}

/** Solid white panel. The default surface for lists and detail blocks. */
export function Card({
  padding = "md",
  interactive = false,
  className,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        "rounded-3xl border border-black/[0.06] bg-white shadow-card",
        interactive &&
          "transition-shadow duration-150 hover:shadow-pop hover:border-black/10",
        PADDING[padding],
        className
      )}
      {...props}
    />
  );
}

/** Frosted panel over the ambient background — sidebar, header, hero blocks. */
export function GlassCard({
  padding = "md",
  className,
  ...props
}: Omit<CardProps, "interactive">) {
  return (
    <div
      className={cn("glass rounded-4xl", PADDING[padding], className)}
      {...props}
    />
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned slot: a button, a link, an icon. */
  action?: ReactNode;
  className?: string;
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: CardHeaderProps) {
  return (
    <div
      className={cn("flex items-start justify-between gap-4", className)}
    >
      <div className="min-w-0">
        <h3 className="text-base font-bold tracking-tight text-ink">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
